import React, { useState } from 'react';
import { X, CreditCard } from 'lucide-react';
interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (paymentData: PaymentData) => void;
  currentCard?: {
    type: string;
    last4: string;
    expiry: string;
  };
}
export interface PaymentData {
  cardNumber: string;
  cardName: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
}
export function PaymentMethodModal({
  isOpen,
  onClose,
  onSave,
  currentCard
}: PaymentMethodModalProps) {
  const [formData, setFormData] = useState<PaymentData>({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });
  const [errors, setErrors] = useState<Partial<PaymentData>>({});
  if (!isOpen) return null;
  const handleChange = (field: keyof PaymentData, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const chunks = cleaned.match(/.{1,4}/g) || [];
    return chunks.join(' ').substring(0, 19); // Max 16 digits + 3 spaces
  };
  const handleCardNumberChange = (value: string) => {
    const formatted = formatCardNumber(value.replace(/\D/g, ''));
    handleChange('cardNumber', formatted);
  };
  const validate = () => {
    const newErrors: Partial<PaymentData> = {};
    if (!formData.cardNumber || formData.cardNumber.replace(/\s/g, '').length < 13) {
      newErrors.cardNumber = 'Valid card number required';
    }
    if (!formData.cardName.trim()) {
      newErrors.cardName = 'Cardholder name required';
    }
    if (!formData.expiryMonth || parseInt(formData.expiryMonth) < 1 || parseInt(formData.expiryMonth) > 12) {
      newErrors.expiryMonth = 'Valid month required';
    }
    if (!formData.expiryYear || formData.expiryYear.length !== 2) {
      newErrors.expiryYear = 'Valid year required';
    }
    if (!formData.cvv || formData.cvv.length < 3) {
      newErrors.cvv = 'Valid CVV required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
      onClose();
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-5225" data-mp="2qac8lfzaayui">
      {/* Overlay */}
      <div className="absolute inset-0 bg-[#0F172A] bg-opacity-50" onClick={onClose} data-id="element-5226" data-mp="2v8nfbhua8t8i" />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto" data-id="element-5227" data-mp="2z9yyp5mkci89">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]" data-id="element-5228" data-mp="342s1vp2dvii8">
          <div className="flex items-center gap-3" data-id="element-5229" data-mp="27ijfllk7tdms">
            <div className="w-10 h-10 rounded-full bg-[#E0F2FE] flex items-center justify-center" data-id="element-5230" data-mp="2bko6wdn6xy69">
              <CreditCard className="w-5 h-5 text-[#0EA5E9]" data-id="element-5231" data-mp="24155ysdzrk9e" />
            </div>
            <h2 className="text-xl font-semibold text-[#0F172A]" data-id="element-5232" data-mp="23z14si7h8zct">
              Update Payment Method
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#475569] transition-colors" data-id="element-5233" data-mp="1v0786ru5c8z9">
            <X className="w-5 h-5" data-id="element-5234" data-mp="1l2j4vdih5t5h" />
          </button>
        </div>

        {/* Current Card Info */}
        {currentCard && <div className="p-6 bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-5235" data-mp="1gbary7hxrpnk">
            <div className="text-sm text-[#475569] mb-2" data-id="element-5236" data-mp="1g9zoy9wbelxi">Current Card</div>
            <div className="flex items-center gap-3" data-id="element-5237" data-mp="1bczku4j0zt91">
              <CreditCard className="w-5 h-5 text-[#94A3B8]" data-id="element-5238" data-mp="17bo0adf0dy7p" />
              <div data-id="element-5239" data-mp="179ba5cz3khjk">
                <div className="text-sm font-medium text-[#0F172A]" data-id="element-5240" data-mp="12brx2ge4yat7">
                  {currentCard.type} ending in {currentCard.last4}
                </div>
                <div className="text-xs text-[#94A3B8]" data-id="element-5241" data-mp="xdqedwkmgdi9">
                  Expires {currentCard.expiry}
                </div>
              </div>
            </div>
          </div>}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-5242" data-mp="32dhpiofc1qyn">
          {/* Card Number */}
          <div data-id="element-5243" data-mp="2np4r7ksrvw0z">
            <label className="block text-sm font-medium text-[#475569] mb-2" data-id="element-5244" data-mp="2nnae2snqt87g">
              Card Number
            </label>
            <input type="text" value={formData.cardNumber} onChange={e => handleCardNumberChange(e.target.value)} placeholder="1234 5678 9012 3456" className={`w-full h-12 px-4 border rounded-md bg-white text-[#0F172A] text-[15px] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all ${errors.cardNumber ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-5245" data-mp="2eoyi0b43xi7m" />
            {errors.cardNumber && <div className="text-sm text-[#EF4444] mt-1" data-id="element-5246" data-mp="2zp87bzspblw">
                {errors.cardNumber}
              </div>}
          </div>

          {/* Cardholder Name */}
          <div data-id="element-5247" data-mp="1sfi0ow37ziw4">
            <label className="block text-sm font-medium text-[#475569] mb-2" data-id="element-5248" data-mp="1sh2tag5z4rrc">
              Cardholder Name
            </label>
            <input type="text" value={formData.cardName} onChange={e => handleChange('cardName', e.target.value)} placeholder="Name on card" className={`w-full h-12 px-4 border rounded-md bg-white text-[#0F172A] text-[15px] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all ${errors.cardName ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-5249" data-mp="1nf3pj56bnstv" />
            {errors.cardName && <div className="text-sm text-[#EF4444] mt-1" data-id="element-5250" data-mp="2l5i56ttt6tyg">
                {errors.cardName}
              </div>}
          </div>

          {/* Expiry and CVV */}
          <div className="grid grid-cols-3 gap-4" data-id="element-5251" data-mp="2g3swvlxz0ue2">
            {/* Expiry Month */}
            <div data-id="element-5252" data-mp="qr7gcld7rzso">
              <label className="block text-sm font-medium text-[#475569] mb-2" data-id="element-5253" data-mp="qssbac3fxkr2">
                Month
              </label>
              <input type="text" value={formData.expiryMonth} onChange={e => {
              const value = e.target.value.replace(/\D/g, '').substring(0, 2);
              handleChange('expiryMonth', value);
            }} placeholder="MM" maxLength={2} className={`w-full h-12 px-4 border rounded-md bg-white text-[#0F172A] text-[15px] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all ${errors.expiryMonth ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-5254" data-mp="1bg9tk66rbo26" />
              {errors.expiryMonth && <div className="text-xs text-[#EF4444] mt-1" data-id="element-5255" data-mp="1yed04ehpacb9">Required</div>}
            </div>

            {/* Expiry Year */}
            <div data-id="element-5256" data-mp="28697yh9l9gso">
              <label className="block text-sm font-medium text-[#475569] mb-2" data-id="element-5257" data-mp="286sg569fsqti">
                Year
              </label>
              <input type="text" value={formData.expiryYear} onChange={e => {
              const value = e.target.value.replace(/\D/g, '').substring(0, 2);
              handleChange('expiryYear', value);
            }} placeholder="YY" maxLength={2} className={`w-full h-12 px-4 border rounded-md bg-white text-[#0F172A] text-[15px] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all ${errors.expiryYear ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-5258" data-mp="2d6n7tc11dkfm" />
              {errors.expiryYear && <div className="text-xs text-[#EF4444] mt-1" data-id="element-5259" data-mp="p7bdk27e2t5g">Required</div>}
            </div>

            {/* CVV */}
            <div data-id="element-5260" data-mp="k7yi4t2gcdr9">
              <label className="block text-sm font-medium text-[#475569] mb-2" data-id="element-5261" data-mp="k9jbifd15qnj">
                CVV
              </label>
              <input type="text" value={formData.cvv} onChange={e => {
              const value = e.target.value.replace(/\D/g, '').substring(0, 4);
              handleChange('cvv', value);
            }} placeholder="123" maxLength={4} className={`w-full h-12 px-4 border rounded-md bg-white text-[#0F172A] text-[15px] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all ${errors.cvv ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-5262" data-mp="ffylb9hoi0yw" />
              {errors.cvv && <div className="text-xs text-[#EF4444] mt-1" data-id="element-5263" data-mp="2j33rauyb3ge">Required</div>}
            </div>
          </div>

          {/* Security Note */}
          <div className="bg-[#F0F9FF] border border-[#BFDBFE] rounded-md p-3" data-id="element-5264" data-mp="7j9034c61e4t">
            <div className="text-xs text-[#1E40AF]" data-id="element-5265" data-mp="v78q221nl0ic">
              🔒 Your payment information is encrypted and secure. We never
              store your full card details.
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4" data-id="element-5266" data-mp="l8szc06jft9d">
            <button type="button" onClick={onClose} className="flex-1 h-12 px-6 border border-[#CBD5E1] rounded-md text-[#475569] font-medium hover:bg-[#F1F5F9] transition-colors" data-id="element-5267" data-mp="q8ozfoc470jn">
              Cancel
            </button>
            <button type="submit" className="flex-1 h-12 px-6 bg-[#0EA5E9] text-white font-medium rounded-md hover:bg-[#0284C7] active:scale-[0.98] transition-all" data-id="element-5268" data-mp="3azftmi0v9uzv">
              Update Card
            </button>
          </div>
        </form>
      </div>
    </div>;
}